/**
 * Whether a signed-in account is FULLY SET UP: the only accounts that may see contact and
 * rights-holder data (owner decision, 2026-10-13). Everyone else gets the signed-out view: signed-out
 * visitors, blocked accounts (pending approval, rejected, suspended) and unfinished sign-ups.
 *
 * The rule, per role, for an account that is not blocked:
 *   admin       always set up
 *   seller      set up once it has a label (`label_tag` not empty) or is active: an organization's
 *               member (Believe's and Felt's teams include some) has no label of its own and works
 *               through its organization's labels; self-serve onboarding makes a seller active only
 *               with a label invite, so an active seller has been vetted
 *   buyer       set up once approved (`status` is 'active')
 *   negotiator  set up once active (`status` is 'active')
 *   anything else, NULL included: not set up
 *
 * This is the UI's copy of the rule. The backend helper and the database function behind
 * migration 20261013055000_set_up_accounts_only.sql enforce it, and all three must agree. A copy
 * that answers "set up" where they don't would ask for rows the database then refuses, so every
 * doubt here answers "not set up": a missing or unreadable profile fails CLOSED.
 *
 * It is the app's entry rule too (`appEntryFor`): the app is for fully set-up accounts, because the
 * database hides the catalog from everyone else. OnboardingGuard, the login page, onboarding's own
 * "already done" check and the share pages' "Go to app" button all ask it.
 */
import { isAccessBlockedStatus } from "@/lib/account-status";

/** The profile fields the rule reads. Any `profiles` row satisfies it. */
export interface AccountSetupFields {
  role?: string | null;
  status?: string | null;
  label_tag?: string | null;
}

/**
 * Who is looking, for the pages that show contact and rights data:
 *   signed-out  no session
 *   set-up      a fully set-up account: everything, as before
 *   incomplete  signed in but not set up, including a missing or unreadable profile
 *   pending     waiting for an admin's approval
 *   blocked     rejected or suspended
 */
export type ViewerAccess = "signed-out" | "set-up" | "incomplete" | "pending" | "blocked";

/** True only for an account that may see contact and rights-holder data. */
export function accountFullySetUp(profile: AccountSetupFields | null | undefined): boolean {
  if (!profile) return false;
  if (isAccessBlockedStatus(profile.status)) return false;
  switch (profile.role) {
    case "admin":
      return true;
    case "seller":
      // Trimmed, as the backend reads label_tag everywhere: a blank label is no label.
      return (
        (typeof profile.label_tag === "string" && profile.label_tag.trim() !== "") ||
        profile.status === "active"
      );
    case "buyer":
    case "negotiator":
      return profile.status === "active";
    default:
      return false;
  }
}

/**
 * Where a signed-in account goes on its way into the app:
 *   app         fully set up: the app
 *   blocked     pending approval, rejected or suspended: the waiting page (/pending-approval), as
 *               before. Never the wizard: an application already refused is not re-made there.
 *   onboarding  everyone else, a missing profile included: the onboarding wizard
 *
 * So a draft buyer who created an organization but never finished goes back to the wizard (its
 * database policies hide the catalog from it), while a draft seller with a label and the draft
 * admin go in, as `accountFullySetUp` says.
 */
export type AppEntry = "app" | "blocked" | "onboarding";

export function appEntryFor(profile: AccountSetupFields | null | undefined): AppEntry {
  if (profile && isAccessBlockedStatus(profile.status)) return "blocked";
  return accountFullySetUp(profile) ? "app" : "onboarding";
}

/**
 * Classify a viewer. `profileError` is any failure to read the profile: it answers "incomplete"
 * (fail closed), never "set-up", whatever `profile` holds.
 */
export function viewerAccessFor(
  hasUser: boolean,
  profile: AccountSetupFields | null | undefined,
  profileError?: unknown
): ViewerAccess {
  if (!hasUser) return "signed-out";
  if (profileError !== undefined && profileError !== null) return "incomplete";
  if (!profile) return "incomplete";
  if (profile.status === "pending_approval") return "pending";
  if (isAccessBlockedStatus(profile.status)) return "blocked";
  return accountFullySetUp(profile) ? "set-up" : "incomplete";
}
